// auditpm-v6
import React, { useEffect, useState } from 'react'
import { supabase, AUDIT_TYPES } from '../lib/supabase'
// Single source of truth shared with the public form — so the builder can never seed a divergent set
// that drops audit_scope / application_type / expiry_date (which CT17 + the New/Renewal lifecycle need).
import { DEFAULT_QUESTIONNAIRES as DEFAULT_QUESTIONS } from '../lib/questionnaireDefaults'

export default function Questionnaires() {
  const [activeType, setActiveType] = useState(AUDIT_TYPES.BIZSAFE)
  const [questions, setQuestions] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [editIdx, setEditIdx] = useState(null)
  const [editQ, setEditQ] = useState(null)
  const [pendingEnquiries, setPendingEnquiries] = useState([])

  useEffect(() => { loadQuestions(activeType) }, [activeType])

  // Sent enquiry links not yet converted into a quotation (the Dashboard "New enquiries" count).
  useEffect(() => {
    supabase.from('enquiry_links').select('id,recipient_name,recipient_email,audit_type,created_at,converted_at,source')
      .order('created_at', { ascending: false })
      // Outreach-engine links (source='outreach', thousands over time) are tracked on the Prospects
      // page — only office-sent links belong in this panel.
      .then(({ data }) => setPendingEnquiries((data || []).filter(e => !e.converted_at && e.source !== 'outreach')))
  }, [])
  function daysAgo(d) { try { return Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000)) } catch (e) { return 0 } }

  async function loadQuestions(type) {
    setQuestions(null)
    const { data } = await supabase.from('questionnaire_templates')
      .select('*').eq('audit_type', type).maybeSingle()
    if (data?.questions) {
      setQuestions(data.questions)
    } else {
      setQuestions(DEFAULT_QUESTIONS[type] || [])
    }
  }

  async function saveQuestions() {
    setSaving(true)
    const { data: existing } = await supabase.from('questionnaire_templates')
      .select('id').eq('audit_type', activeType).maybeSingle()
    if (existing) {
      await supabase.from('questionnaire_templates').update({ questions }).eq('audit_type', activeType)
    } else {
      await supabase.from('questionnaire_templates').insert({ audit_type: activeType, questions })
    }
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  function moveQ(idx, dir) {
    const qs = [...questions]
    const newIdx = idx + dir
    if (newIdx < 0 || newIdx >= qs.length) return
    ;[qs[idx], qs[newIdx]] = [qs[newIdx], qs[idx]]
    setQuestions(qs)
  }

  function deleteQ(idx) {
    setQuestions(qs => qs.filter((_, i) => i !== idx))
  }

  function startEdit(idx) {
    setEditIdx(idx)
    setEditQ(idx === 'new' ? { id: '', label: '', type: 'text', required: false, options: '' } : { ...questions[idx], options: (questions[idx].options || []).join('\n') })
  }

  function saveEdit() {
    const q = { ...editQ, options: editQ.options ? editQ.options.split('\n').map(s => s.trim()).filter(Boolean) : undefined }
    if (!q.options?.length) delete q.options
    if (editIdx === 'new') {
      setQuestions(qs => [...qs, q])
    } else {
      setQuestions(qs => qs.map((x, i) => i === editIdx ? q : x))
    }
    setEditIdx(null)
    setEditQ(null)
  }

  const typeKeys = Object.values(AUDIT_TYPES)

  return (
    <div>
      {editIdx !== null && (
        <div className="modal-overlay" onClick={() => setEditIdx(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editIdx === 'new' ? 'Add question' : 'Edit question'}</h2>
              <button onClick={() => setEditIdx(null)} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text3)' }}>×</button>
            </div>
            <div className="field"><label>Field ID (no spaces)</label>
              <input value={editQ.id} onChange={e => setEditQ(q => ({ ...q, id: e.target.value.replace(/\s/g, '_') }))} /></div>
            <div className="field"><label>Question label</label>
              <input value={editQ.label} onChange={e => setEditQ(q => ({ ...q, label: e.target.value }))} /></div>
            <div className="grid-2">
              <div className="field"><label>Field type</label>
                <select value={editQ.type} onChange={e => setEditQ(q => ({ ...q, type: e.target.value }))}>
                  {['text','number','date','textarea','select','yesno','multicheck'].map(t => <option key={t} value={t}>{t === 'multicheck' ? 'multicheck (tickboxes)' : t}</option>)}
                </select>
              </div>
              <div className="field" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                <input type="checkbox" id="req" checked={editQ.required} onChange={e => setEditQ(q => ({ ...q, required: e.target.checked }))} style={{ width: 'auto' }} />
                <label htmlFor="req" style={{ margin: 0, fontSize: 13 }}>Required</label>
              </div>
            </div>
            {(editQ.type === 'select' || editQ.type === 'multicheck') && (
              <div className="field"><label>Options (one per line){editQ.type === 'multicheck' ? ' — clients can tick multiple' : ''}</label>
                <textarea value={editQ.options} onChange={e => setEditQ(q => ({ ...q, options: e.target.value }))} rows={5} /></div>
            )}
            <div className="modal-footer">
              <button onClick={() => setEditIdx(null)}>Cancel</button>
              <button className="primary" onClick={saveEdit} disabled={!editQ.id || !editQ.label}>Save</button>
            </div>
          </div>
        </div>
      )}

      <div id="pending-enquiries" className="card" style={{ marginBottom: 16 }}>
        <h2 className="card-title">
          Sent enquiries — awaiting response
          <span style={{ marginLeft: 8, fontSize: 13, background: 'var(--bg2)', color: 'var(--text2)', padding: '2px 8px', borderRadius: 12 }}>{pendingEnquiries.length}</span>
        </h2>
        <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 8 }}>Questionnaire links sent but not yet converted into a quotation.</p>
        {pendingEnquiries.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>No enquiries awaiting response.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {pendingEnquiries.map(e => (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 0.8fr 0.8fr', gap: 8, fontSize: 13, padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontWeight: 500 }}>{e.recipient_name || e.recipient_email || '—'}</span>
                <span style={{ color: 'var(--text2)' }}>{e.audit_type || '—'}</span>
                <span style={{ color: 'var(--text2)' }}>sent {daysAgo(e.created_at)}d ago</span>
                <span style={{ color: 'var(--text2)' }}>awaiting response</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="page-header">
        <h1>Questionnaire builder</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => startEdit('new')}>+ Add question</button>
          <button className="primary" onClick={saveQuestions} disabled={saving}>
            {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save template'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {typeKeys.map(t => (
          <button key={t} onClick={() => setActiveType(t)}
            style={{ fontWeight: activeType === t ? 500 : 400, background: activeType === t ? 'var(--text)' : 'var(--bg)', color: activeType === t ? 'var(--bg)' : 'var(--text)' }}>
            {t}
          </button>
        ))}
      </div>

      <div className="notice notice-info" style={{ marginBottom: 16, fontSize: 13 }}>
        These questions will be shown to clients when they fill the questionnaire for <strong>{activeType}</strong>. Changes take effect immediately on new form links.
      </div>

      {!questions && <div style={{ color: 'var(--text3)' }}>Loading…</div>}
      {questions && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {questions.map((q, i) => (
            <div key={q.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
              <div style={{ color: 'var(--text3)', fontSize: 13, minWidth: 20 }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 13 }}>{q.label}</div>
                <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>
                  {q.id} · {q.type}
                  {q.required && <span className="badge badge-info" style={{ marginLeft: 6 }}>required</span>}
                  {q.options && <span style={{ marginLeft: 6 }}>({q.options.length} options)</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button className="sm" onClick={() => moveQ(i, -1)} disabled={i === 0}>↑</button>
                <button className="sm" onClick={() => moveQ(i, 1)} disabled={i === questions.length - 1}>↓</button>
                <button className="sm" onClick={() => startEdit(i)}>Edit</button>
                <button className="sm danger" onClick={() => deleteQ(i)}>×</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}